import {
  ApprovalDecisionState,
  ApprovalStrength,
  CustomEventDataType,
  type ApprovalKind,
  type TurnDegradationPart,
} from '@myre/app-agent-domain';
import { type Translated } from '@myre/common-i18n';
import { type MetricResultBlock } from '@myre/app-agent-domain/registry/result-block';
import { type ChatContextParams } from '@myre/app-agent-generated/types/ChatContextParams';
import { type CtaAction } from '@myre/app-agent-generated/types/CtaAction';
import { type McpServerInfo } from '@myre/app-agent-generated/types/McpServerInfo';

import { type UIMessage, type UIMessagePart, type UITools } from 'ai';

/**
 * Activity hint surfaced on a chat window's minimized tab:
 * - `processing`: the agent is generating a reply
 * - `unread`: a reply landed while the window was minimized
 * - `question`: the agent finished by asking a clarification question (your turn)
 * - `idle`: nothing to flag
 */
export type ChatActivityStatus = 'idle' | 'processing' | 'unread' | 'question';

/**
 * Single CTA item received from the backend generate_ctas tool.
 */
export interface CtaItem {
  label: string;
  /** The backend action this button triggers — the generated SDK enum, not a loose string. */
  actionType: CtaAction;
  payload: Record<string, unknown>;
  confirmMessage?: string;
}

/**
 * Single selectable option in a clarification question (from generate_question).
 */
export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
  /**
   * Self-contained, fully-disambiguated message replayed verbatim as the user's
   * answer when this option is picked — authored by the agent, never interpreted
   * here. Falls back to the label only for older payloads without it.
   */
  value?: string;
}

/**
 * Structured clarification question the agent asks before launching an analysis.
 */
export interface ClarificationQuestion {
  prompt: string;
  options: QuestionOption[];
  multiple?: boolean;
  /**
   * The recorded answer (an option's resolved value, or the typed free text),
   * persisted onto the part once picked. Absent while pending. On a reload it is
   * the single source of truth for the card's resolved rendering — the matching
   * option is highlighted and the card is locked, exactly as it was live.
   */
  answer?: string;
}

/**
 * A user's answer to a clarification question.
 */
export interface ClarificationAnswer {
  /** Sent to the agent (the option's resolved value, or the typed free text). */
  value: string;
  /** Shown in the user bubble (the option label, or the typed free text). */
  label: string;
}

/**
 * A destination the agent offers, rendered as a link beside its answer. Nothing
 * waits on it: the user opens it, stays, or leaves it alone, and the agent never
 * routes on its own.
 */
export interface NavigateProposal {
  /** The in-app URL to route to when the user opens it. */
  url: string;
  /** Human-readable destination shown on the card. */
  label: string;
  /**
   * Whether the user has already decided (opened or stayed), persisted onto the
   * part once made. Absent while untouched. On a reload it locks the card —
   * exactly as it was left — and stops the "don't ask again" effect from
   * re-navigating.
   */
  decided?: boolean;
}

/**
 * Raw tool result backing a citation step — the actual data the worker's
 * prose was derived from.
 */
export interface CitationEvidence {
  tool: string;
  args_summary: string;
  /** Human-readable description of the call (per-tool describer, when one exists) */
  call_label?: string;
  result_excerpt: string;
  /** When the result was a JSON array larger than the excerpt budget: its full length */
  total_items?: number;
}

/**
 * One source step within a citation, pointing back to a worker's output
 * excerpt and the raw tool evidence behind it (absent for tool-less workers).
 */
export interface CitationStep {
  step_index: number;
  worker_name: string;
  excerpt: string;
  evidence?: CitationEvidence[];
}

/**
 * A single grounded citation emitted by the backend in a data-citation part.
 */
export interface Citation {
  marker: number;
  statement: string;
  steps: CitationStep[];
}

/**
 * A single operation the agent's commit will run, as previewed in the dry-run
 * plan (mirrors `set_contract`'s `operations`). `destructive` marks an op that
 * deletes or overwrites existing data — surfaced prominently on the card.
 *
 * Canonical wire shape — emitted server-side in the `data-approval` part and
 * consumed verbatim by the card (single source of truth).
 */
/**
 * One line of the approval diff. Every field is a finished string in the
 * reader's language: the server owns the wording, the labels, the date format
 * and the currency unit, because it is the side that holds the two values and
 * knows which unit each is counted in.
 */
export interface ApprovalPlanChange {
  /** What is changing, named for a reader: the element, then the field. */
  field: string;
  /** What the platform holds today, or the wording for "nothing recorded". */
  current: string;
  /** What would be written, or the wording for "removed". */
  proposed: string;
  /** Where the proposed value comes from, as a sentence. */
  source: string;
  /** Whether applying this line destroys something already recorded. */
  destructive: boolean;
}

export interface ApprovalPlanOperation {
  /** The operation verb (e.g. `create`, `update`, `delete`). */
  op: string;
  /** What the operation acts on (e.g. `rent`, `indexationRegime`). */
  target: string;
  /** Whether the operation deletes or overwrites existing data. */
  destructive: boolean;
  /** One-line human summary of the change. */
  summary: string;
  /** The element's local plan `ref`, when the operation addresses one. */
  ref?: string;
}

/**
 * A finding the reconciled state raised during the preview. An `error` blocks
 * the commit; a `warn` is informational (already accepted at preview time).
 *
 * Canonical wire shape — see `ApprovalPlanOperation`.
 */
export interface ApprovalPlanFinding {
  severity: 'error' | 'warn';
  /** Domain finding code (the AM error/integrity code that raised it). */
  code: number;
  message: string;
  /** Elements the finding points at (refs/ids), when the server localizes it. */
  locators?: readonly string[];
}

/**
 * How firmly the card must make the human commit.
 * - `normal`: Confirm dispatches the resume immediately (reversible writes — a
 *   contract draft stays editable).
 * - `strong`: the action is IRREVERSIBLE (e.g. an email leaves). Confirm starts a
 *   `graceSeconds` countdown (a progress-bar "Annuler l'envoi" button) and only
 *   dispatches when it elapses; cancelling within the window aborts just this
 *   attempt (nothing is sent) and re-arms the Confirm.
 */
interface ApprovalConfirmation {
  strength: ApprovalStrength;
  /** Grace window before a `strong` confirmation dispatches. Default 3. */
  graceSeconds?: number;
}

/** A document attachable to the email: its S3 `key` (identity) + display `name`. */
export interface EmailAttachmentRef {
  key: string;
  name: string;
}

/**
 * The exact email an `send-approval` will send, rendered VERBATIM on the card so
 * the human validates byte-for-byte what leaves (and what is persisted in the
 * chat). These are the same values the server hashes into the approval token, so
 * what is displayed === what is signed === what is sent: the model cannot show one
 * message and send another. `body` is plain text (rendered as escaped text with
 * line breaks preserved); `bcc` is the connected user, always added server-side.
 */
export interface ApprovalEmailMessage {
  to: readonly string[];
  cc?: readonly string[];
  bcc: string;
  subject: string;
  body: string;
  /** The currently attached document keys (parallel to `attachmentNames`). */
  attachments?: readonly string[];
  /** Resolved (owner-verified) filenames of the attachments, parallel to `attachments`. */
  attachmentNames?: readonly string[];
}

/**
 * The previewed plan shown to the human for confirmation. Plain, already-computed
 * data (the dry-run preview the agent produced) — rendered as escaped text, never
 * HTML.
 */
export interface ApprovalPlan {
  /**
   * For an email send: the exact message to display verbatim (the artifact the
   * human validates). Present only on `send-approval`; its content matches the
   * token hash and the sent + persisted message.
   */
  message?: ApprovalEmailMessage;
  /**
   * For an email send: the addresses the agent surfaced from the data this
   * conversation — the only set the edit modal may add recipients from, so an
   * edited `to`/`cc` stays within DB-read addresses. Present only on
   * `send-approval`.
   */
  availableRecipients?: readonly string[];
  /**
   * For an email send: the documents surfaced this conversation that the edit
   * modal may attach (each is owner-verified server-side at send). Present only
   * on `send-approval`.
   */
  availableAttachments?: readonly EmailAttachmentRef[];
  /** The contract being edited, when the commit targets an existing one. */
  contractId?: number;
  /**
   * Card headline (a question), e.g. "Confirmer cette modification du bail ?" or
   * "Envoyer cet e-mail ?". Falls back to a generic prompt for older payloads.
   */
  prompt?: string;
  /** How firmly to confirm. Absent ⇒ a `normal` (reversible) confirmation. */
  confirmation?: ApprovalConfirmation;
  /**
   * The user-bubble text shown when the human CONFIRMS — authored per action by
   * the tool that raised the gate (e.g. "Modification confirmée" for a contract,
   * "Envoi confirmé" for an email), so it is never a context-blind hardcode. Falls
   * back to a generic label for older payloads.
   */
  confirmLabel?: string;
  /** The note shown when the human CANCELS — authored per action (e.g. "Envoi
   * annulé — aucun e-mail n'a été envoyé."). Generic fallback otherwise. */
  cancelLabel?: string;
  /** Headline describing what the commit will do. */
  summary: string;
  /**
   * The DIFF the commit would apply, one line per field: what the platform holds
   * today, what would replace it, and where that value came from. Rendered
   * server-side in the reader's language and units — the card prints it, it
   * never composes or formats it. Absent for an email send, and absent from
   * payloads produced before the card carried a diff.
   */
  changes?: readonly ApprovalPlanChange[];
  /** The previewed operations (contract commit). Absent for an email send. */
  operations?: readonly ApprovalPlanOperation[];
  /** Preview findings (contract commit). Absent for an email send. */
  findings?: readonly ApprovalPlanFinding[];
}

/**
 * A human-in-the-loop approval surfaced by the agent's graph `interrupt()` at a
 * destructive action. The card renders `plan` for the user to inspect; the
 * `approvalToken` is server-signed and plan-bound — it rides in the data part
 * for the resume ONLY (replayed verbatim through the `resume` body channel) and
 * is NEVER shown to the user nor handed to the LLM. `kind` is the action it gates:
 * `commit-approval` (a contract write) or `send-approval` (an email send).
 */
export interface ApprovalRequest {
  kind: ApprovalKind;
  approvalToken: string;
  plan: ApprovalPlan;
  /**
   * The recorded human decision, persisted onto the part once made (server-set on
   * Confirm-resume, or via the cancel notification). Absent while pending. On a
   * reload it is the single source of truth for the card's resolved rendering, so
   * a reloaded card looks exactly like it did live.
   */
  decision?: ApprovalDecisionState;
}

/**
 * A user's Confirm on an approval card — carries the token to replay on resume,
 * plus the context-appropriate bubble text to show as the human's choice (from
 * the plan's `confirmLabel`).
 */
export interface ApprovalDecision {
  approvalToken: string;
  label?: string;
  editedEmailContent?: EditedEmailContent;
}

/**
 * The user's edits to an email draft before confirming the send: subject/body
 * free text, plus a recipient list they may trim or extend. Recipients can only
 * ever be addresses the agent already surfaced from the data (the modal offers
 * `ApprovalPlan.availableRecipients`); the server re-checks this, so an edit can
 * never introduce a free-typed address. `bcc` is never editable.
 */
export interface EditedEmailContent {
  subject: string;
  body: string;
  to: readonly string[];
  cc: readonly string[];
  /** The document keys to attach (each owner-verified server-side at send). */
  attachments: readonly string[];
}

/**
 * Heads-up that the user is close to their period AI allowance. `message` is
 * already localized and fully worded server-side (it carries the renewal date);
 * `usedPercent` is the share of the cap consumed, for the gauge on the card.
 */
export interface QuotaWarning {
  message: string;
  usedPercent: number;
}

/**
 * Custom data types for the Myre chat.
 * Each key maps to a `data-{key}` part type with typed data payload.
 */
export type MyreDataTypes = {
  status: { type: CustomEventDataType.Status; message: string };
  thought: { type: CustomEventDataType.Thought; delta: string };
  trace: { traceId: string };
  file: { data: { fileName: string; mimeType: string; url: string } };
  cta: { type: CustomEventDataType.Cta; ctas: CtaItem[] };
  question: {
    type: CustomEventDataType.Question;
    question: ClarificationQuestion;
  };
  navigate: { type: CustomEventDataType.Navigate; navigate: NavigateProposal };
  citation: { type: CustomEventDataType.Citation; citations: Citation[] };
  approval: { type: CustomEventDataType.Approval; approval: ApprovalRequest };
  result: { type: CustomEventDataType.Result; result: MetricResultBlock };
  'quota-warning': {
    type: CustomEventDataType.QuotaWarning;
  } & QuotaWarning;
  degraded: {
    type: CustomEventDataType.Degraded;
  } & TurnDegradationPart;
};

/**
 * Per-message metadata (client-side display only).
 */
interface MyreMessageMetadata {
  /**
   * Text shown in the user bubble instead of the raw message content — used
   * when a clarification option is picked, so the bubble shows the clean label
   * while the agent receives the option's full resolved value.
   */
  displayLabel?: string;
}

/**
 * Typed UIMessage using Myre custom data types.
 */
export type MyreUiMessage = UIMessage<MyreMessageMetadata, MyreDataTypes>;

/**
 * Typed UIMessagePart using Myre custom data types.
 */
export type MyreUiMessagePart = UIMessagePart<MyreDataTypes, UITools>;

/**
 * Chat status types from the AI SDK
 */
export type ChatStatus = 'streaming' | 'submitted' | 'ready' | 'error';

/**
 * Format part types for Markdown parsing
 */
export enum FormatPartType {
  Markdown = 'markdown',
  Code = 'code',
  Html = 'html',
  Copy = 'copy',
}

/**
 * Predefined message suggestion for quick actions
 */
export interface ChatSuggestion {
  /**
   * The text to display for the suggestion
   */
  text: string;
  /**
   * Optional icon to display (emoji or icon class)
   */
  icon?: string;
}

/**
 * Welcome screen configuration
 */
export interface ChatWelcomeConfig {
  /**
   * The welcome message to display
   */
  message: string;
  /**
   * URL of the icon/image to display above the welcome message
   */
  iconUrl?: string;
  /**
   * Alternative: emoji or icon character to display
   */
  icon?: string;
  /**
   * Predefined message suggestions
   */
  suggestions?: readonly ChatSuggestion[];
}

/**
 * Chat input configuration
 */
export interface ChatInputConfig {
  /**
   * Placeholder text for the input field
   */
  placeholder?: string;
  /**
   * Whether to enable file upload functionality
   */
  enableUpload?: boolean;
  /**
   * Accepted file types for upload (e.g., 'image/*,.pdf')
   */
  acceptedFileTypes?: string;
  /**
   * Footer disclaimer configuration
   */
  footer?: ChatInputFooterConfig;
  /**
   * Available MCP servers (tools) from the selected agent
   */
  mcpServers?: readonly McpServerInfo[];
  /**
   * Whether the current user is an admin (controls visibility of debug option)
   */
  isAdmin?: boolean;
}

/**
 * Footer disclaimer configuration
 */
interface ChatInputFooterConfig {
  /**
   * Whether to show the footer
   */
  show: boolean;
  /**
   * The disclaimer text to display
   */
  text?: string;
  /**
   * Optional link URL for "learn more" or similar
   */
  linkUrl?: string;
  /**
   * Optional link text
   */
  linkText?: string;
}

/**
 * The ids the context zone displays: every id the agent reads a scope from,
 * except customerId, which scopes every call and so is sent, never displayed.
 */
export type ChatContextKey = Exclude<keyof ChatContextParams, 'customerId'>;

/**
 * Context item for display in the context zone
 */
export interface ChatContextItem {
  /**
   * The context id the item stands for (e.g., 'portfolioId', 'propertyId')
   */
  key: ChatContextKey;
  /**
   * Display label for the context item
   */
  label: Translated;
  /**
   * The id the item points at
   */
  value: string | number;
  /**
   * The entity's name, when the app could resolve it for that id
   */
  name?: string;
  /**
   * Optional icon (emoji or icon class)
   */
  icon?: string;
}
